import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, X, Send, PhoneForwarded } from "lucide-react";
import { FundleLogo } from "./Brand";

export default function PublicAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [handoffMode, setHandoffMode] = useState(false);
  const [handoff, setHandoff] = useState({ name: "", contact: "" });
  const bottomRef = useRef(null);
  const [sessionId] = useState(() => {
    const saved = sessionStorage.getItem("talentos_public_session");
    if (saved) return saved;
    const sid = `pub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("talentos_public_session", sid);
    return sid;
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/agent/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.token) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.token };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      toast.error("Assistant unavailable: " + e.message);
    } finally {
      setStreaming(false);
    }
  };

  const submitHandoff = async () => {
    if (!handoff.name || !handoff.contact) return toast.error("Name and phone/email required");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/handoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, ...handoff, channel: "whatsapp" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("A recruiter has been notified on WhatsApp (simulated) and will reach out shortly.");
      setHandoffMode(false);
      setHandoff({ name: "", contact: "" });
    } catch (e) {
      toast.error("Handoff failed: " + e.message);
    }
  };

  return (
    <>
      {!open && (
        <button data-testid="public-assistant-open" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#E05A47] text-white px-6 py-3.5 text-sm font-bold rounded-full shadow-xl hover:bg-[#C74936] transition-colors duration-150">
          <MessageSquare size={16} /> Ask TalentOS AI
        </button>
      )}
      {open && (
        <div data-testid="public-assistant-panel" className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] bg-white border shadow-2xl flex flex-col rounded-xl" style={{ height: "540px" }}>
          <div className="bg-[#0A251D] text-white px-4 py-3 flex items-center justify-between rounded-t-xl">
            <div className="flex items-center gap-2.5">
              <FundleLogo height={14} />
              <div className="border-l border-white/20 pl-2.5">
                <div className="font-heading font-bold text-sm leading-none">TalentOS AI Assistant</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#A8C3B4] mt-0.5">ICS Group · Powered by Fundle</div>
              </div>
            </div>
            <button data-testid="public-assistant-close" onClick={() => setOpen(false)} className="hover:text-[#A8C3B4] transition-colors duration-150"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="public-chat-messages">
            {messages.length === 0 && (
              <div className="text-sm text-muted-foreground leading-relaxed pt-4">
                Hi! I can tell you about open roles, the hiring process, benefits and life at the ICS Group. What would you like to know?
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0A251D] text-white" : "bg-secondary"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          {handoffMode ? (
            <div className="border-t p-3 space-y-2" data-testid="public-handoff-form">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#0A251D]">Talk to a recruiter</div>
              <input data-testid="handoff-name-input" value={handoff.name} onChange={(e) => setHandoff({ ...handoff, name: e.target.value })}
                placeholder="Your name" className="w-full border px-3 py-2 text-sm" />
              <input data-testid="handoff-contact-input" value={handoff.contact} onChange={(e) => setHandoff({ ...handoff, contact: e.target.value })}
                placeholder="WhatsApp number or email" className="w-full border px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button data-testid="handoff-submit-button" onClick={submitHandoff}
                  className="flex-1 bg-[#0A251D] text-white py-2 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Request Callback</button>
                <button onClick={() => setHandoffMode(false)} className="border px-3 text-xs uppercase tracking-widest hover:bg-secondary transition-colors duration-150">Back</button>
              </div>
            </div>
          ) : (
            <div className="border-t p-3">
              <div className="flex gap-2">
                <input data-testid="public-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()} disabled={streaming}
                  placeholder="Ask about roles, benefits, process…"
                  className="flex-1 border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A251D]" />
                <button data-testid="public-chat-send" onClick={send} disabled={streaming}
                  className="bg-[#0A251D] text-white px-3 hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-60"><Send size={14} /></button>
              </div>
              <button data-testid="public-handoff-open" onClick={() => setHandoffMode(true)}
                className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#0A251D] transition-colors duration-150">
                <PhoneForwarded size={11} /> Talk to a human recruiter
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
